/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { GetCookie, RemoveCookie, SetCookie } from '../customHook/useCookies'

export type APIErrorType = {
  error: {
    code?: string
    details: string
  }
}

export const InternalError: APIErrorType = {
  error: {
    code: '500',
    details: 'Internal error during request',
  },
}

export const onFirstRequest = (config: AxiosRequestConfig) => {
  const configCopy = { ...config }
  configCopy.headers = {
    'Content-type': 'application/json; charset=UTF-8',
  }
  return configCopy
}
export const onFirstPrivateRequest = (config: AxiosRequestConfig) => {
  const token = GetCookie('token')
  const configCopy = { ...config }
  configCopy.headers = {
    'Content-type': 'application/json; charset=UTF-8',
    Authorization: `Bearer ${token}`,
  }
  return configCopy
}

export const onFulfilledRequest = (response: AxiosResponse) => response
// export const onRejectedResponse = (_error: any): any => Promise.reject(_error.response.data)
export const onRejectedResponse = async (error: any): Promise<any> => {
  if (error.response) {
    const originalRequest = error.config
    const status = error.response.status

    // Only attempt token refresh for 401 Unauthorized errors
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true // Prevent infinite retry loops
      const refreshToken = GetCookie('refreshToken')

      if (refreshToken) {
        try {
          // Make a request to refresh the access token using the refresh token
          const response = await axios.post(
            `${import.meta.env.VITE_BASE_URL_API}/api/v1/auth/refresh-token`,
            {
              refreshToken,
            },
          )

          // Update the access token in the original request headers
          SetCookie('token', response.data.data.accessToken)
          originalRequest.headers.Authorization = `Bearer ${response.data.data.accessToken}`

          // Retry the original request with the updated access token
          return axios(originalRequest)
        } catch (refreshError: any) {
          // eslint-disable-next-line no-console
          console.error('Error refresh token', refreshError)
          RemoveCookie('token')
          RemoveCookie('refreshToken')
          // If the error is not due to an expired access token, redirect to /adminpage/login
          window.location.href = '/'
          return Promise.reject(error.response.data)
        }
      } else {
        // No refresh token available, clear tokens and reject
        RemoveCookie('token')
        RemoveCookie('refreshToken')
        return Promise.reject(error.response.data)
      }
    }

    // For all other HTTP errors (400, 403, 404, 500, etc.), just reject with the response data
    return Promise.reject(error.response.data)
  } else if (error.request) {
    return Promise.reject(InternalError)
  } else {
    return Promise.reject({
      error: {
        details: error.message,
      },
    })
  }
}

// Helper function to create headers with optional custom token
export const createAuthHeaders = (customToken?: string) => {
  const token = customToken || GetCookie('token')

  const headers: Record<string, string> = {
    'Content-type': 'application/json; charset=UTF-8',
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}

// Function to make authenticated requests with custom token
export const makeAuthenticatedRequestV2 = async (
  method: 'get' | 'post' | 'put' | 'delete',
  url: string,
  customToken?: string,
  data?: any,
) => {
  const config: AxiosRequestConfig = {
    headers: createAuthHeaders(customToken),
    method,
    url,
    data,
  }

  return axios(config)
}

export const publicRequestV2 = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL_API}/api/v1`,
})
export const privateRequestV2 = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL_API}/api/v1`,
})

publicRequestV2.interceptors.request.use(onFirstRequest, onRejectedResponse)
publicRequestV2.interceptors.response.use(onFulfilledRequest, onRejectedResponse)

privateRequestV2.interceptors.request.use(onFirstPrivateRequest, onRejectedResponse)
privateRequestV2.interceptors.response.use(onFulfilledRequest, onRejectedResponse)
